import type { CodeKind, PortalRole } from './types';

/** Group managers may issue temp codes valid for at most 7 days (spec 02 note 6). */
export const GROUP_MANAGER_MAX_CODE_MS = 7 * 24 * 60 * 60 * 1000;

export interface CodeWindowInput {
  kind: CodeKind;
  validFrom: string; // datetime-local or ISO
  validUntil: string; // required: temp codes always have an end date
  role: PortalRole;
  now?: Date;
}

export type CodeWindowResult =
  | { ok: true; validFrom: string; validUntil: string }
  | { ok: false; error: string };

export function validateCodeWindow(input: CodeWindowInput): CodeWindowResult {
  const now = input.now ?? new Date();
  if (!input.validFrom) return { ok: false, error: 'Vul een begintijd in.' };
  if (!input.validUntil) return { ok: false, error: 'Een tijdelijke code heeft altijd een eindtijd.' };
  const from = new Date(input.validFrom);
  const until = new Date(input.validUntil);
  if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime())) {
    return { ok: false, error: 'Begin- of eindtijd klopt niet.' };
  }
  if (until.getTime() <= from.getTime()) {
    return { ok: false, error: 'De eindtijd moet na de begintijd liggen.' };
  }
  if (until.getTime() <= now.getTime()) {
    return { ok: false, error: 'De eindtijd ligt in het verleden.' };
  }
  if (input.role === 'group_manager' && until.getTime() - from.getTime() > GROUP_MANAGER_MAX_CODE_MS) {
    return { ok: false, error: 'Als groepsbeheerder kun je een code maximaal 7 dagen geldig maken.' };
  }
  return { ok: true, validFrom: from.toISOString(), validUntil: until.toISOString() };
}

export const CODE_KIND_LABEL: Record<CodeKind, string> = {
  offline_once: 'Eenmalig',
  offline_window: 'Tijdvenster',
  online_custom: 'Eigen code (online)',
};

export type CodeState = 'active' | 'scheduled' | 'expired' | 'revoked';

export function codeState(
  c: { valid_from: string; valid_until: string; revoked_at: string | null },
  now = new Date(),
): CodeState {
  if (c.revoked_at) return 'revoked';
  if (new Date(c.valid_until).getTime() <= now.getTime()) return 'expired';
  if (new Date(c.valid_from).getTime() > now.getTime()) return 'scheduled';
  return 'active';
}

/** Is the code usable at some point during the given local calendar day? */
export function codeValidOnDay(
  c: { valid_from: string; valid_until: string; revoked_at: string | null },
  day: Date,
): boolean {
  if (c.revoked_at) return false;
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const end = start + 24 * 60 * 60 * 1000;
  return new Date(c.valid_from).getTime() < end && new Date(c.valid_until).getTime() > start;
}
